import { apiService } from '../utils/api';

export interface UnifiedContact {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  relationship?: string;
  source: 'manual' | 'csv' | 'google' | 'outlook' | 'executor_invite' | 'memory';
  notes?: string;
  externalId?: string;
  active?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface BulkImportResult {
  success: boolean;
  imported: number;
  duplicates: number;
  errors: number;
  contacts: UnifiedContact[];
  duplicateContacts: Array<{
    contact: Partial<UnifiedContact>;
    existing: UnifiedContact;
  }>;
  errorDetails: Array<{
    contact: Partial<UnifiedContact>;
    error: string;
  }>;
}

export interface DuplicateCheckResult {
  success: boolean;
  isDuplicate: boolean;
  contact: UnifiedContact | null;
}

class UnifiedContactService {
  private cache: UnifiedContact[] | null = null;
  private cacheTimestamp: number = 0;
  private readonly CACHE_TTL = 5 * 60 * 1000; // 5 minutes

  // Per-estate cache for executor read-only views.  Keyed by estateId.
  private estateCache = new Map<string, { contacts: UnifiedContact[]; ts: number }>();

  /**
   * Fetch contacts for a specific estate owner, for use on executor routes.
   * Uses a separate per-estate cache so executor views never share the
   * planner's global contact cache (and vice-versa).
   *
   * @param estateId  - The estate owner's user ID (= route :estateId)
   * @param forceRefresh - Skip cache and fetch from backend
   */
  async getContactsForEstate(estateId: string, forceRefresh = false): Promise<UnifiedContact[]> {
    const now = Date.now();
    const cached = this.estateCache.get(estateId);

    if (!forceRefresh && cached && now - cached.ts < this.CACHE_TTL) {
      console.log(`[UnifiedContactService] Returning cached estate contacts for ${estateId}`);
      return cached.contacts;
    }

    try {
      const response = await apiService.request(
        `/api/settlement/${estateId}/unified-contacts`,
        { method: 'GET' }
      );

      if (response.success && Array.isArray(response.contacts)) {
        this.estateCache.set(estateId, { contacts: response.contacts, ts: now });
        console.log(`[UnifiedContactService] Fetched ${response.contacts.length} contacts for estate ${estateId}`);
        return response.contacts;
      }

      throw new Error(response.message || 'Failed to fetch estate contacts');
    } catch (error) {
      console.error(`[UnifiedContactService] Error fetching estate contacts for ${estateId}:`, error);
      // Return stale cache if available rather than crashing the page.
      if (cached) {
        console.warn('[UnifiedContactService] Returning stale estate cache due to error');
        return cached.contacts;
      }
      throw error;
    }
  }

  /**
   * Invalidate the per-estate contact cache.
   * Call this when the active estate changes in the executor portal.
   *
   * @param estateId - Invalidate only this estate's cache; omit to clear all.
   */
  invalidateEstateCache(estateId?: string): void {
    if (estateId) {
      this.estateCache.delete(estateId);
      console.log(`[UnifiedContactService] Estate cache invalidated for ${estateId}`);
    } else {
      this.estateCache.clear();
      console.log('[UnifiedContactService] All estate caches invalidated');
    }
  }

  /**
   * Get all contacts for the current user
   * @param forceRefresh - Skip cache and fetch from backend
   */
  async getAllContacts(forceRefresh: boolean = false): Promise<UnifiedContact[]> {
    // Check cache validity
    const now = Date.now();
    if (!forceRefresh && this.cache && (now - this.cacheTimestamp) < this.CACHE_TTL) {
      console.log('[UnifiedContactService] Returning cached contacts');
      return this.cache;
    }

    try {
      const response = await apiService.request('/api/unified-contacts', {
        method: 'GET'
      });

      if (response.success && Array.isArray(response.contacts)) {
        this.cache = response.contacts;
        this.cacheTimestamp = now;
        console.log(`[UnifiedContactService] Fetched ${response.contacts.length} contacts from backend`);
        return response.contacts;
      }

      throw new Error(response.message || 'Failed to fetch contacts');
    } catch (error) {
      console.error('[UnifiedContactService] Error fetching contacts:', error);
      // Return cached data if available, even if expired
      if (this.cache) {
        console.warn('[UnifiedContactService] Returning stale cache due to error');
        return this.cache;
      }
      throw error;
    }
  }

  /**
   * Get a single contact by ID
   */
  async getContactById(contactId: string): Promise<UnifiedContact> {
    try {
      const response = await apiService.request(`/api/unified-contacts/${contactId}`, {
        method: 'GET'
      });

      if (response.success && response.contact) {
        return response.contact;
      }

      throw new Error(response.message || 'Contact not found');
    } catch (error) {
      console.error('[UnifiedContactService] Error fetching contact:', error);
      throw error;
    }
  }

  /**
   * Create a new contact
   */
  async createContact(contactData: Omit<UnifiedContact, 'id' | 'createdAt' | 'updatedAt'>): Promise<UnifiedContact> {
    try {
      const response = await apiService.request('/api/unified-contacts', {
        method: 'POST',
        body: JSON.stringify(contactData)
      });

      if (response.success && response.contact) {
        // Invalidate cache
        this.invalidateCache();
        console.log('[UnifiedContactService] Contact created:', response.contact.id);
        return response.contact;
      }

      throw new Error(response.message || 'Failed to create contact');
    } catch (error: unknown) {
      console.error('[UnifiedContactService] Error creating contact:', error);

      // Handle duplicate error specifically
      if (error.message?.includes('already exists') || error.code === 'DUPLICATE_CONTACT') {
        const duplicateError: unknown = new Error(error.message);
        duplicateError.code = 'DUPLICATE_CONTACT';
        throw duplicateError;
      }

      throw error;
    }
  }

  /**
   * Update an existing contact
   */
  async updateContact(contactId: string, updates: Partial<UnifiedContact>): Promise<UnifiedContact> {
    try {
      const response = await apiService.request(`/api/unified-contacts/${contactId}`, {
        method: 'PUT',
        body: JSON.stringify(updates)
      });

      if (response.success && response.contact) {
        // Invalidate cache
        this.invalidateCache();
        console.log('[UnifiedContactService] Contact updated:', contactId);
        return response.contact;
      }

      throw new Error(response.message || 'Failed to update contact');
    } catch (error) {
      console.error('[UnifiedContactService] Error updating contact:', error);
      throw error;
    }
  }

  /**
   * Delete a contact
   */
  async deleteContact(contactId: string): Promise<boolean> {
    try {
      const response = await apiService.request(`/api/unified-contacts/${contactId}`, {
        method: 'DELETE'
      });

      if (response.success) {
        // Invalidate cache
        this.invalidateCache();
        console.log('[UnifiedContactService] Contact deleted:', contactId);
        return true;
      }

      throw new Error(response.message || 'Failed to delete contact');
    } catch (error) {
      console.error('[UnifiedContactService] Error deleting contact:', error);
      throw error;
    }
  }

  /**
   * Bulk import contacts (for CSV, OAuth sync, etc.)
   */
  async bulkImport(contacts: Array<Partial<UnifiedContact>>, source: UnifiedContact['source']): Promise<BulkImportResult> {
    try {
      const response = await apiService.request('/api/unified-contacts/bulk-import', {
        method: 'POST',
        body: JSON.stringify({ contacts, source })
      });

      if (response.success) {
        // Invalidate cache
        this.invalidateCache();
        console.log(`[UnifiedContactService] Bulk import complete: ${response.imported} imported, ${response.duplicates} duplicates, ${response.errors} errors`);
        return response as BulkImportResult;
      }

      throw new Error(response.message || 'Failed to import contacts');
    } catch (error) {
      console.error('[UnifiedContactService] Error bulk importing contacts:', error);
      throw error;
    }
  }

  /**
   * Search contacts by name, email, or phone
   */
  async searchContacts(query: string): Promise<UnifiedContact[]> {
    if (!query || query.trim().length === 0) {
      return [];
    }

    try {
      const response = await apiService.request(`/api/unified-contacts/search?q=${encodeURIComponent(query)}`, {
        method: 'GET'
      });

      if (response.success && Array.isArray(response.contacts)) {
        return response.contacts;
      }

      throw new Error(response.message || 'Search failed');
    } catch (error) {
      console.error('[UnifiedContactService] Error searching contacts:', error);
      throw error;
    }
  }

  /**
   * Get contacts filtered by relationship
   */
  async getContactsByRelationship(relationship: string): Promise<UnifiedContact[]> {
    try {
      const response = await apiService.request(`/api/unified-contacts/relationship/${encodeURIComponent(relationship)}`, {
        method: 'GET'
      });

      if (response.success && Array.isArray(response.contacts)) {
        return response.contacts;
      }

      throw new Error(response.message || 'Failed to fetch contacts');
    } catch (error) {
      console.error('[UnifiedContactService] Error fetching contacts by relationship:', error);
      throw error;
    }
  }

  /**
   * Check if a contact with the given email already exists
   */
  async checkDuplicate(email: string): Promise<DuplicateCheckResult> {
    try {
      const response = await apiService.request('/api/unified-contacts/check-duplicate', {
        method: 'POST',
        body: JSON.stringify({ email })
      });

      if (response.success) {
        return {
          success: true,
          isDuplicate: response.isDuplicate,
          contact: response.contact
        };
      }

      throw new Error(response.message || 'Duplicate check failed');
    } catch (error) {
      console.error('[UnifiedContactService] Error checking duplicate:', error);
      throw error;
    }
  }

  /**
   * Invalidate the local cache
   */
  invalidateCache(): void {
    this.cache = null;
    this.cacheTimestamp = 0;
    console.log('[UnifiedContactService] Cache invalidated');
  }

  /**
   * Get full name from contact
   */
  getFullName(contact: UnifiedContact): string {
    return `${contact.firstName} ${contact.lastName}`.trim();
  }

  /**
   * Format contact for display
   */
  formatContactDisplay(contact: UnifiedContact): string {
    const name = this.getFullName(contact);
    if (contact.email && contact.phone) {
      return `${name} (${contact.email}, ${contact.phone})`;
    } else if (contact.email) {
      return `${name} (${contact.email})`;
    } else if (contact.phone) {
      return `${name} (${contact.phone})`;
    }
    return name;
  }
}

export const unifiedContactService = new UnifiedContactService();
